import { useCallback } from 'react';
import { useLocalStorage } from './useLocalStorage';

/**
 * Remembers the id of the most recent in-flight LoRA training job for a given
 * key (a character / lab-profile id), so a progress modal can be closed and
 * later reopened without losing the running job.
 *
 * Training runs entirely server-side: starting a run returns a durable job id
 * and progress is observed by polling `GET /lora/jobs`. Closing a modal only
 * stops the client-side poll — it never cancels the job. By persisting the job
 * id here we can resume polling that same job when the modal reopens, instead of
 * dropping the user back to the config/confirm screen.
 *
 * The id is cleared once the job reaches a terminal state (so a finished run is
 * not resumed) — see {@link clear}.
 */
export interface BackgroundTrainingJob {
  /** The remembered in-flight job id for this key, or null if none. */
  pendingJobId: string | null;
  /** Remember a freshly started job id so it survives a close/reopen. */
  remember: (jobId: string) => void;
  /** Forget the remembered job (call when it reaches a terminal state). */
  clear: () => void;
}

const STORAGE_KEY = 'training_pending_jobs';

type PendingMap = Record<string, string>;

export function useBackgroundTrainingJob(key: string): BackgroundTrainingJob {
  const [map, setMap] = useLocalStorage<PendingMap>(STORAGE_KEY, {});

  const remember = useCallback(
    (jobId: string) => {
      setMap((prev) => ({ ...prev, [key]: jobId }));
    },
    [key, setMap]
  );

  const clear = useCallback(() => {
    setMap((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, [key, setMap]);

  return { pendingJobId: map[key] ?? null, remember, clear };
}
